import { LEVEL_DEG } from '@camper-diary/shared';

/** Raggio utile della fiala, in unità del viewBox, e fondoscala in gradi. */
const VIAL_R = 78;
const VIAL_MAX_DEG = 3;

type Props = {
  roll: number;
  pitch: number;
};

/**
 * La livella a bolla: la stessa che sta sul cruscotto, ma viva. È l'elemento
 * centrale della pagina perché si legge in un colpo d'occhio, da lontano, di
 * sfuggita — che è come la si guarda davvero, mentre si manovra.
 */
export function LevelVial({ roll, pitch }: Props) {
  const magnitude = Math.hypot(roll, pitch);
  const centred = magnitude <= LEVEL_DEG;

  // Scala a radice: comprime i gradi grossi, dilata i decimi vicino allo zero,
  // che è la zona in cui si deve poter leggere una differenza.
  const r = VIAL_R * Math.sqrt(Math.min(1, magnitude / VIAL_MAX_DEG));

  // La bolla galleggia: va verso il lato ALTO, come in una livella vera.
  const ux = magnitude > 0.001 ? -roll / magnitude : 0;
  const uy = magnitude > 0.001 ? -pitch / magnitude : 0;

  const cx = 100 + r * ux;
  const cy = 100 + r * uy;

  return (
    <div className="vial-wrap">
      <svg className="vial" viewBox="0 0 200 200" role="img" aria-labelledby="vial-title">
        <title id="vial-title">Livella a bolla: posizione attuale del camper</title>

        <circle className="body" cx="100" cy="100" r="92" />

        {/* Cerchi di graduazione. La scala non è lineare ma a radice: così il
            mezzo grado, che è la differenza fra dormire bene e male, occupa
            spazio invece di sparire nel centro. Ogni cerchio porta il suo valore. */}
        <circle className="grad" cx="100" cy="100" r="31.8" />
        <circle className="grad" cx="100" cy="100" r="45.0" />
        <circle className="grad" cx="100" cy="100" r="63.7" />
        <circle className="grad" cx="100" cy="100" r="78.0" />

        {/* Le tacche stanno sulla diagonale libera, non sugli assi: sugli assi
            passa la bolla, e un numero sotto la bolla non si legge. */}
        <text className="grad-label" x="79" y="80">0.5°</text>
        <text className="grad-label" x="70" y="71">1°</text>
        <text className="grad-label" x="57" y="58">2°</text>
        <text className="grad-label" x="47" y="48">3°</text>

        {/* Tre tacche invece di quattro: in cima al posto della quarta c'è
            l'unica informazione che serve davvero, da che parte guarda il camper. */}
        <path className="cross" d="M100 178v14M8 100h14M178 100h14" />
        <text className="nose" x="100" y="22" textAnchor="middle">MUSO</text>

        <circle
          className={centred ? 'bubble centred' : 'bubble'}
          cx={cx.toFixed(2)}
          cy={cy.toFixed(2)}
          r="15"
        />
        <circle className="glint" cx={(cx - 5).toFixed(2)} cy={(cy - 5).toFixed(2)} r="4.5" />
      </svg>
    </div>
  );
}
